// util
import { ZonedMoment } from "@util/time-zone";

export type TypeEnum = "Add" | "Move" | "UndoMove" | "Remove";

export interface IEventKey {
  externalEventId: string;
}

export interface IEventTime {
  dateOrDateTime: string;
  millis: number;
}

interface IEventParentEdge {
  node: IEventParent;
  cursor: string;
}

interface IEventParent {
  id: string;
  externalEventId: string;
}

export interface IEvent {
  isClockwiseEvent: boolean | null;
  isAllDay: boolean | null;
  startTime: IEventTime;
  endTime: IEventTime;
  eventKey: IEventKey;
}

export interface IEventMapObj<E extends IEvent> {
  normal: EventPositioner<E>;
  defragged: EventPositioner<E>;
}

export interface IEventMap<E extends IEvent> {
  [s: string]: IEventMapObj<E>;
}

interface IEventMoveSuggestionMap {
  [key: string]: IEventMoveSuggestion;
}

interface IEventMoveSuggestion {
  id: string;
  eventKey: IEventKey;
  originalTime: IEventTime;
  proposedTime: IEventTime;
  addedMinutes: number;
  type: TypeEnum;
}

export interface IDefragSuggestion {
  eventMoveSuggestion: IEventMoveSuggestion;
}

function getDayIndex(start: ZonedMoment, days: ZonedMoment[]) {
  return days.findIndex((day) => start.isSame(day, "d"));
}

/**
 * Maps a defragSuggestion to an externalEventId.
 *
 * @param suggestions
 */
function generateEventMoveSuggestionMap(suggestions?: IDefragSuggestion[]) {
  const eventMoveSuggestionMap: IEventMoveSuggestionMap = {};

  // no suggestions, return early
  if (!suggestions || (suggestions as any) === false || !suggestions.length || !suggestions[0]) {
    return eventMoveSuggestionMap;
  }

  suggestions.forEach((suggestion) => {
    const eventMoveSuggestion = suggestion.eventMoveSuggestion;

    const { externalEventId } = eventMoveSuggestion.eventKey;

    // note, if there are multiple suggestions per externalEventId, this will overwrite
    // but Mike tell's me that it's okay to just use the last one, so this should be fine
    eventMoveSuggestionMap[externalEventId] = eventMoveSuggestion;
  });
  return eventMoveSuggestionMap;
}

/**
 * Generates a new event based on the move suggestion.
 * TODO: THIS IS NOT CAREFUL ENOUGH ABOUT TIMEZONES!
 *
 * @param event
 * @param eventMoveSuggestion
 */
function generateDefraggedEvent<E extends IEvent>(
  event: E,
  eventMoveSuggestion: IEventMoveSuggestion,
) {
  if (!eventMoveSuggestion) {
    return Object.assign({}, event);
  }

  if (eventMoveSuggestion.type === "Remove") {
    return event;
  }
  const startTime = eventMoveSuggestion.proposedTime;
  const eventLength = event.endTime.millis - event.startTime.millis;
  const addedMillis = eventMoveSuggestion.addedMinutes * 60000 || 0;
  const proposedEndMillis = eventMoveSuggestion.proposedTime.millis + eventLength + addedMillis;
  const endTime = Object.assign({}, event.endTime, {
    millis: proposedEndMillis,
    dateOrDateTime: new ZonedMoment(proposedEndMillis).toISOString(),
  });

  return Object.assign({}, event, { startTime, endTime });
}

function sortPositioners<E extends IEvent>(positioners: EventPositioner<E>[]) {
  return positioners.sort((positioner1, positioner2) => {
    return positioner1.start.unix() - positioner2.start.unix();
  });
}

function collatePositioners<E extends IEvent>(
  normal: EventPositioner<E>[],
  defragged: EventPositioner<E>[],
) {
  const collated: IEventMap<E> = {};
  normal.forEach((positioner) => {
    collated[positioner.event.eventKey.externalEventId] = {
      normal: positioner,
      defragged: positioner,
    };
  });
  defragged.forEach((defraggedPositioner) => {
    const collatedEvent = collated[defraggedPositioner.event.eventKey.externalEventId];
    if (!collatedEvent) {
      console.warn("no normal positioner event in collated map");
      collated[defraggedPositioner.event.eventKey.externalEventId] = {
        normal: defraggedPositioner,
        defragged: defraggedPositioner,
      };
      return;
    }
    collatedEvent.defragged = defraggedPositioner;
  });

  return collated;
}

export function getPositioners<E extends IEvent>(
  events: E[],
  days: ZonedMoment[],
  suggestionEdges?: IDefragSuggestion[],
  addedPreEventParentEdges?: IEventParentEdge[],
  minHeightInMinutes = 30,
  showSmartHolds = true,
) {
  const eventMoveSuggestionMap = generateEventMoveSuggestionMap(suggestionEdges);
  // create addedPreEventMap = EventIds[]
  const normalPositioners: EventPositioner<E>[] = [];
  const defraggedPositioners: EventPositioner<E>[] = [];

  const addedPreEventIDs: { [key: string]: boolean } = {};
  if (addedPreEventParentEdges) {
    addedPreEventParentEdges.map((edge) => {
      addedPreEventIDs[edge.node.externalEventId] = true;
    });
  }

  events.forEach((event) => {
    // this enables the smart-hold toggle
    // remove them from the bucket if smartHolds isn't on
    // could probably do this at a better stage...
    if (!showSmartHolds && event.isClockwiseEvent) {
      return;
    }

    // add every event positioner to the normal, non-defragged set
    const normalPositioner = new EventPositioner(
      event,
      days,
      false,
      minHeightInMinutes,
      addedPreEventIDs[event.eventKey.externalEventId],
    );
    if (normalPositioner.dayIndex >= 0) {
      // nuts to these stupid out of range days
      normalPositioners.push(normalPositioner);
    }

    // check if this event has a proposed time
    const eventMoveSuggestion = eventMoveSuggestionMap[event.eventKey.externalEventId];
    let defraggedPositioner: EventPositioner<E>;
    if (eventMoveSuggestion) {
      // if there is a proposed time, add a positioner with a defragged event
      const defraggedEvent = generateDefraggedEvent(event, eventMoveSuggestion);
      defraggedPositioner = new EventPositioner(
        defraggedEvent,
        days,
        true,
        minHeightInMinutes,
        addedPreEventIDs[event.eventKey.externalEventId],
        eventMoveSuggestion.type,
      );
    } else {
      // otherwise, add a new regular positioner
      defraggedPositioner = new EventPositioner(
        event,
        days,
        false,
        minHeightInMinutes,
        addedPreEventIDs[event.eventKey.externalEventId],
      );
    }

    if (defraggedPositioner.dayIndex >= 0) {
      defraggedPositioners.push(defraggedPositioner);
    }
  });

  // need to process each set of positioners in one batch b/c order matters
  const normalPositioned = setHorizontalPosition(normalPositioners);
  const defraggedPositioned = setHorizontalPosition(defraggedPositioners);

  // and then collate them into sets of normal and defragged by externalEventId
  return collatePositioners(normalPositioned, defraggedPositioned);
}

export function getNormalPositioners<E extends IEvent>(
  events: E[],
  days: ZonedMoment[],
  minHeightInMinutes = 30,
  showSmartHolds = true,
  getMinMaxHours = () => ({ minStart: 0, maxEnd: 48 }),
) {
  // create addedPreEventMap = EventIds[]
  const normalPositioners: EventPositioner<E>[] = [];

  events.forEach((event) => {
    // this enables the smart-hold toggle
    // remove them from the bucket if smartHolds isn't on
    // could probably do this at a better stage...
    if (!showSmartHolds && event.isClockwiseEvent) {
      return;
    }

    // add every event positioner to the normal, non-defragged set
    const normalPositioner = new EventPositioner(
      event,
      days,
      false,
      minHeightInMinutes,
      undefined,
      undefined,
      getMinMaxHours,
    );
    if (normalPositioner.dayIndex >= 0) {
      // nuts to these stupid out of range days
      normalPositioners.push(normalPositioner);
    }
  });

  // need to process set of positioners in one batch b/c order matters
  return setHorizontalPosition(normalPositioners);
}

/**
 * Set top/left positioning.
 * This is the trickiest because you need to know what's overlapping.
 * Based on algorithm described here: http://stackoverflow.com/questions/11311410
 *
 * @param eventPositioners
 */
function setHorizontalPosition<E extends IEvent>(eventPositioners: EventPositioner<E>[]) {
  let columns: EventPositioner<E>[][] = [];
  let lastEventEnding: ZonedMoment | undefined;

  // Create an array of all events
  const positioners = sortPositioners(eventPositioners);

  // Iterate over the sorted array
  positioners.forEach((eventPositioner) => {
    // ignore removed events
    if (eventPositioner.suggestionType === "Remove") {
      return;
    }

    // Check if a new event group needs to be started
    if (!!lastEventEnding && eventPositioner.start.isSameOrAfter(lastEventEnding)) {
      // The latest event is later than any of the event in the
      // current group. There is no overlap. Output the current
      // event group and start a new event group.
      columns = sortColumns(columns);
      packEvents(columns);
      columns = []; // This starts new event group.
      lastEventEnding = undefined;
    }

    // Try to place the event inside the existing columns
    let placed = false;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (first30MinsCollide(col[col.length - 1], eventPositioner)) {
        break;
      } else {
        for (let y = col.length - 1; y >= 0; y--) {
          const currEP = col[y];
          if (collidesWith(currEP, eventPositioner)) {
            if (currEP.floatOffset) {
              placed = true;
              eventPositioner.floatOffset = currEP.floatOffset + 5;
              break;
            } else {
              eventPositioner.floatOffset = 5;
              placed = true;
              break;
            }
          } else if (currEP.floatOffset) {
            continue;
          } else {
            placed = true;
            break;
          }
        }

        if (placed) {
          col.push(eventPositioner);
          break;
        }
      }
    }

    // It was not possible to place the event. Add a new column
    // for the current event group.
    if (!placed) {
      columns.push([eventPositioner]);
    }

    // Remember the latest event end time of the current group.
    // This is later used to determine if a new groups starts.
    const epDiff = eventPositioner.end.diff(eventPositioner.start, "minutes");
    const adjustedEPEnd = eventPositioner.end
      .clone()
      .add(
        epDiff < eventPositioner.minHeightInMinutes
          ? eventPositioner.minHeightInMinutes - epDiff
          : 0,
        "minutes",
      );
    if (!lastEventEnding || adjustedEPEnd.isSameOrAfter(lastEventEnding)) {
      lastEventEnding = adjustedEPEnd;
    }
  });

  if (columns.length > 0) {
    columns = sortColumns(columns);
    packEvents(columns);
  }

  return positioners;
}

/**
 * Sorts columns so that the 'heavier' ones sit towards the left.
 *
 * @param columns
 */
function sortColumns<E extends IEvent>(columns: EventPositioner<E>[][]) {
  return columns.sort((aCols, bCols) => {
    const aColDiffs = aCols
      .map((ep) => ep.end.diff(ep.start, "milliseconds"))
      .reduce((prev, curr) => prev + curr, 0);
    const bColDiffs = bCols
      .map((ep) => ep.end.diff(ep.start, "milliseconds"))
      .reduce((prev, curr) => prev + curr, 0);

    return bColDiffs - aColDiffs;
  });
}

/**
 * Function does the layout for a group of events.
 *
 * @param columns
 */
function packEvents<E extends IEvent>(columns: EventPositioner<E>[][]) {
  const n = columns.length;
  for (let i = 0; i < n; i++) {
    const col = columns[i];
    for (let j = 0; j < col.length; j++) {
      const bubble = col[j];
      const colWidth = bubble.colWidth / n;
      const colSpan = expandEvent(bubble, i, columns);
      const width = colWidth * colSpan;
      const calcLeft = bubble.colLeft + colWidth * i;
      bubble.left = Math.min(calcLeft, bubble.colLeft + bubble.colWidth - width);
      bubble.width = width;
      bubble.position = i;
    }
  }
}

/**
 * Check if first 30 mins of event a collides with b start.
 * @param a
 * @param b
 */
function first30MinsCollide<E extends IEvent>(a: EventPositioner<E>, b: EventPositioner<E>) {
  return Math.abs(a.start.diff(b.start, "minutes")) < 30;
}

/**
 * Check if two events collide.
 * @param a
 * @param b
 */
function collidesWith<E extends IEvent>(a: EventPositioner<E>, b: EventPositioner<E>) {
  return a.bottom > b.top && a.top < b.bottom;
}

/**
 * Expand events at the far right to use up any remaining space.
 * Checks how many columns the event can expand into, without
 * colliding with other events. Step 5 in the algorithm.
 * @param iColumn
 * @param columns
 */
function expandEvent<E extends IEvent>(
  ev: EventPositioner<E>,
  iColumn: number,
  columns: EventPositioner<E>[][],
) {
  let colSpan = 1;

  // To see the output without event expansion, uncomment
  // the line below. Watch column 3 in the output.
  for (let i = iColumn + 1; i < columns.length; i++) {
    const col = columns[i];
    for (let j = 0; j < col.length; j++) {
      const ev1 = col[j];
      if (first30MinsCollide(ev, ev1)) {
        return colSpan;
      }
    }
    colSpan++;
  }
  return colSpan;
}

export class EventPositioner<E extends IEvent> {
  public readonly event: E;
  public readonly start: ZonedMoment;
  public readonly end: ZonedMoment;
  public readonly week: ZonedMoment[];
  public readonly day: ZonedMoment;
  public readonly dayIndex: number;
  public readonly top: number;
  public readonly bottom: number;
  public readonly height: number;
  public readonly colWidth: number;
  public readonly colLeft: number;
  public readonly isDefragged: boolean;
  public readonly isAddedPreEvent: boolean;
  public readonly suggestionType: TypeEnum | undefined;
  public readonly minHeightInMinutes: number;
  public width: number;
  public left: number;
  public position = 0;
  public floatOffset = 0;

  /**
   * Create an event positioner to be used with the event positioning algorithms
   *
   * @param event the event to wrap
   * @param days which days this event can span accross (YYYY-MM-DD)
   * @param isDefragged whether it's defragged
   * @param minHeightInMinutes the min height it must have, in minutes
   * @param isAddedPreEvent whether added pre event
   * @param suggestionType optional suggestion type
   * @param getMinMaxHours a method to determine the min / max slots this can range across, assumes 48 slots
   *                       TODO: may need a safer mechanism here
   */
  constructor(
    event: E,
    days: ZonedMoment[],
    isDefragged?: boolean,
    minHeightInMinutes = 30,
    isAddedPreEvent?: boolean,
    suggestionType?: TypeEnum,
    getMinMaxHours = () => ({ minStart: 0, maxEnd: 48 }),
  ) {
    const { minStart, maxEnd } = getMinMaxHours();

    this.event = event;
    this.isDefragged = isDefragged || false;
    this.isAddedPreEvent = isAddedPreEvent || false;
    this.suggestionType = suggestionType;
    this.minHeightInMinutes = minHeightInMinutes;

    this.week = days.sort();

    this.start = new ZonedMoment(event.startTime.dateOrDateTime);
    this.end = new ZonedMoment(event.endTime.dateOrDateTime);

    // Adjust start and end for events that span multiple days.
    if (days?.length > 0) {
      const firstDay = new ZonedMoment(days[0]).startOf("day");
      if (this.start.isBefore(firstDay)) {
        this.start = firstDay;
      }
      const lastDay = new ZonedMoment(days[days.length - 1]).endOf("day");
      if (this.end.isAfter(lastDay)) {
        this.end = lastDay;
      }
    }

    const startDate = this.start;

    if (event.isAllDay) {
      this.dayIndex = days.findIndex((d) =>
        d.isSame(new ZonedMoment(event.startTime.dateOrDateTime), "days"),
      );
      this.top = 0;
      this.height = 100;
      this.bottom = 0;

      this.day = days[this.dayIndex];
      this.colWidth = 100 / days.length;
      this.colLeft = this.colWidth * this.dayIndex;

      this.width = 100;
      this.left = 0;
    } else {
      this.dayIndex = getDayIndex(startDate, days);

      const top =
        ((this.start.hour() * 2 + this.start.minute() / 30 - minStart) / (maxEnd - minStart)) * 100;
      const bottom =
        (((this.end.day() - this.start.day()) * 48 +
          this.end.hour() * 2 +
          this.end.minute() / 30 -
          minStart) /
          (maxEnd - minStart)) *
        100;

      this.top = Math.max(0, Math.min(100, top));
      this.bottom = Math.max(0, Math.min(100, bottom));
      this.height = Math.min(
        100,
        Math.max((minHeightInMinutes / 1440) * 100, this.bottom - this.top),
      );

      this.day = days[this.dayIndex];
      this.colWidth = 100 / days.length;
      this.colLeft = this.colWidth * this.dayIndex;
      this.width = this.colWidth + 0;
      this.left = this.colLeft + 0;
    }
  }
}
